import { useEffect, useState, useRef } from 'react';
import Header from './Header';
import api from '../api/client';
import './Support.css';

function formatTime(date) {
    if (!date) return '';
    return new Date(date).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function Support({ translations }) {
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const t = (key, fallback) => translations?.[key] || fallback;

    useEffect(() => {
        let active = true;
        api.getSupportMessages()
            .then((res) => {
                if (!active) return;
                setTicket(res?.ticket || null);
                setMessages(res?.messages || []);
            })
            .catch(() => {})
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        const value = text.trim();
        if (!value || sending) return;
        setSending(true);
        try {
            const res = await api.sendSupportMessage(value, 'web');
            setText('');
            if (res?.message) setMessages((prev) => [...prev, res.message]);
        } catch {
            // Ignore
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Header translations={translations} />
            <div className="support-container">
                <h1 className="support-title">{t('support-title', 'Help & Support')}</h1>
                <p className="support-subtitle">
                    {t('support-subtitle', 'Send us a message and our team will get back to you.')}
                </p>

                <div className="support-chat">
                    {loading ? (
                        <div className="loading-page"><div className="spinner" /></div>
                    ) : messages.length === 0 ? (
                        <p className="support-empty">{t('support-empty', 'No messages yet. Start the conversation below.')}</p>
                    ) : (
                        messages.map((m) => {
                            const mine = m.senderType === 'user';
                            return (
                                <div key={m.id} className={`support-row ${mine ? 'mine' : 'theirs'}`}>
                                    <div className="support-bubble">
                                        <span className="support-content">{m.content}</span>
                                        <span className="support-time">{formatTime(m.createdAt)}</span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={bottomRef} />
                </div>

                {ticket?.status && (
                    <p className="support-status">
                        {t('support-status', 'Status')}: <strong>{ticket.status.replace(/_/g, ' ')}</strong>
                    </p>
                )}

                <form className="support-composer" onSubmit={handleSend}>
                    <textarea
                        className="support-input"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={t('support-placeholder', 'Describe your problem...')}
                        rows={3}
                        maxLength={5000}
                    />
                    <button type="submit" className="support-send" disabled={sending || !text.trim()}>
                        {sending ? t('support-sending', 'Sending...') : t('support-send', 'Send')}
                    </button>
                </form>
            </div>
        </>
    );
}
